// Weekly planning: suggest 10 candidates, let the user choose 5 or 7, then arrange the week.
//
// Arrangement rules (all explainable):
//   * Dinners that use ingredients marked "use soon" go first.
//   * Dinners whose fresh kitchen ingredients spoil fastest (fish, meat, leafy greens) go early.
//   * Longer dinners land on the weekend when the week includes one.
//   * The same main protein is never served two nights running when it can be avoided.
// Locked and already-cooked days are never moved.

import { mainProtein, type RankedRecipe } from './ranking';
import { getRecipe } from './recipes';
import { diversify, type Recommendation } from './recommend';
import { INGREDIENTS, lineage, type IngredientId } from './taxonomy';
import { addDays, parseISODate } from '../lib/dates';
import type { SkipReason } from '../state/schema';

export interface PlanEntry {
  date: string; // YYYY-MM-DD (local)
  recipeId: string;
  servings: number;
  locked: boolean;
  status: 'planned' | 'cooked' | 'skipped';
  skipReason: SkipReason | null;
}

export interface Plan {
  startDate: string;
  entries: PlanEntry[];
  createdAt: string;
  offered: string[];
}

export const CANDIDATE_COUNT = 10;
export const PLAN_SIZES = [5, 7] as const;
export type PlanSize = (typeof PLAN_SIZES)[number];

/**
 * Ten defensible candidates: the best-scoring dinners, spread across proteins and cuisines,
 * preferring ones you can make now or are one or two items away from.
 */
export function planCandidates(recs: Recommendation[], exclude: string[] = [], n = CANDIDATE_COUNT): Recommendation[] {
  const pool = recs.filter((r) => !exclude.includes(r.recipe.id));
  const ready = pool.filter((r) => r.makeable || r.missing.length <= 2);
  const rest = pool.filter((r) => !(r.makeable || r.missing.length <= 2));
  const picked = diversify(ready, n);
  if (picked.length < n) picked.push(...diversify(rest, n - picked.length));
  return picked;
}

/** Days from now an ingredient tends to stay good, by kind. Rough ordering only, never shown. */
function shelfRank(id: IngredientId): number {
  const def = INGREDIENTS[id];
  if (def.seafood) return 1;
  if (def.meat) return 2;
  if (['spinach', 'baby_spinach', 'lettuce', 'fresh_basil', 'cilantro', 'mushrooms', 'avocado', 'strawberries', 'blueberries'].includes(id)) return 3;
  if (def.category === 'vegetable' || def.category === 'fruit' || def.category === 'dairy') return 5;
  return 9;
}

export interface ArrangeInput {
  selected: RankedRecipe[];
  startDate: string;
  servings: number;
  kitchen: IngredientId[];
  useSoon: IngredientId[];
  keep?: PlanEntry[];
  offered?: string[];
}

export function arrangeWeek({ selected, startDate, servings, kitchen, useSoon, keep = [], offered = [] }: ArrangeInput): Plan {
  const kitchenSet = new Set(kitchen.flatMap((k) => lineage(k)));
  const soon = new Set(useSoon);
  const urgency = (r: RankedRecipe) => {
    const uses = r.recipe.ingredients.filter((i) => kitchenSet.has(i.id)).map((i) => shelfRank(i.id));
    const usesSoon = r.usesSoon.length || r.recipe.ingredients.some((i) => soon.has(i.id));
    return (usesSoon ? -10 : 0) + (uses.length ? Math.min(...uses) : 9);
  };

  const keepByDate = new Map(keep.map((e) => [e.date, e]));
  const nights = selected.length + keep.length;
  const freeDates: string[] = [];
  for (let i = 0; i < nights; i++) {
    const d = addDays(startDate, i);
    if (!keepByDate.has(d)) freeDates.push(d);
  }

  // Order by urgency, then fill dates; long dinners swap onto weekend dates afterwards.
  const queue = [...selected].sort((a, b) => urgency(a) - urgency(b) || a.recipe.minutes - b.recipe.minutes);
  const assigned = new Map<string, RankedRecipe>();
  freeDates.forEach((d, i) => { if (queue[i]) assigned.set(d, queue[i]); });

  const weekend = freeDates.filter((d) => [0, 6].includes(parseISODate(d).getDay()));
  for (const wd of weekend) {
    const current = assigned.get(wd);
    if (!current) continue;
    // Find the longest non-urgent dinner on a weekday and swap it in.
    let best: string | null = null;
    for (const d of freeDates) {
      if (weekend.includes(d)) continue;
      const r = assigned.get(d)!;
      if (r && r.recipe.minutes >= 35 && r.recipe.minutes > current.recipe.minutes && urgency(r) >= 5) {
        if (!best || r.recipe.minutes > assigned.get(best)!.recipe.minutes) best = d;
      }
    }
    if (best) { const t = assigned.get(best)!; assigned.set(best, current); assigned.set(wd, t); }
  }

  const entries: PlanEntry[] = [];
  for (let i = 0; i < nights; i++) {
    const d = addDays(startDate, i);
    const kept = keepByDate.get(d);
    if (kept) { entries.push(kept); continue; }
    const r = assigned.get(d);
    if (r) entries.push({ date: d, recipeId: r.recipe.id, servings, locked: false, status: 'planned', skipReason: null });
  }
  return { startDate, entries: avoidBackToBackProtein(entries), createdAt: new Date().toISOString(), offered };
}

/** Swap adjacent unlocked planned entries to avoid the same protein two nights in a row. */
export function avoidBackToBackProtein(entries: PlanEntry[]): PlanEntry[] {
  const out = entries.map((e) => ({ ...e }));
  const movable = (e: PlanEntry) => !e.locked && e.status === 'planned';
  for (let i = 1; i < out.length; i++) {
    if (proteinOf(out[i].recipeId) === null || proteinOf(out[i].recipeId) !== proteinOf(out[i - 1].recipeId)) continue;
    for (let j = i + 1; j < out.length; j++) {
      if (!movable(out[i]) || !movable(out[j])) continue;
      if (proteinOf(out[j].recipeId) !== proteinOf(out[i - 1].recipeId)) {
        const a = out[i].recipeId; out[i].recipeId = out[j].recipeId; out[j].recipeId = a;
        break;
      }
    }
  }
  return out;
}

function proteinOf(recipeId: string) {
  const r = getRecipe(recipeId);
  return r ? mainProtein(r) : null;
}

/** Replace one day's dinner with a specific recipe. */
export function replaceEntry(plan: Plan, date: string, recipeId: string): Plan {
  return { ...plan, entries: plan.entries.map((e) => (e.date === date ? { ...e, recipeId, locked: false, status: 'planned', skipReason: null } : e)) };
}

/** Swap a day's dinner with the previous or next day. Dates stay; recipes move. */
export function moveEntry(plan: Plan, date: string, dir: -1 | 1): Plan {
  const i = plan.entries.findIndex((e) => e.date === date);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= plan.entries.length) return plan;
  const a = plan.entries[i], b = plan.entries[j];
  if (a.status !== 'planned' || b.status !== 'planned') return plan;
  const entries = [...plan.entries];
  entries[i] = { ...a, recipeId: b.recipeId, servings: b.servings, locked: b.locked };
  entries[j] = { ...b, recipeId: a.recipeId, servings: a.servings, locked: a.locked };
  return { ...plan, entries };
}

/** Best alternatives for one day, excluding dinners already in the plan. */
export function replacementsFor(plan: Plan, date: string, recs: Recommendation[], n = 5): Recommendation[] {
  const inPlan = new Set(plan.entries.map((e) => e.recipeId));
  const current = plan.entries.find((e) => e.date === date);
  const seed = recs.find((r) => r.recipe.id === current?.recipeId);
  return diversify(recs.filter((r) => !inPlan.has(r.recipe.id) && (r.makeable || r.missing.length <= 3)), n, seed);
}

/** A plan is over once the day after its last entry has arrived. */
export function planEnded(plan: Plan, today: string): boolean {
  return addDays(plan.startDate, plan.entries.length) <= today;
}

export interface PlanReview {
  planned: number;
  cooked: number;
  skipped: number;
  /** Planned days in the past that were neither cooked nor skipped. */
  missed: number;
  skipReasons: Partial<Record<SkipReason, number>>;
  notes: string[];
}

/** What happened to a past plan, in plain words. Used before replanning. */
export function reviewPlan(plan: Plan, today: string): PlanReview {
  const past = plan.entries.filter((e) => e.date < today || e.status !== 'planned');
  const cooked = past.filter((e) => e.status === 'cooked').length;
  const skipped = past.filter((e) => e.status === 'skipped').length;
  const missed = past.filter((e) => e.status === 'planned').length;
  const skipReasons: PlanReview['skipReasons'] = {};
  for (const e of past) if (e.status === 'skipped' && e.skipReason) skipReasons[e.skipReason] = (skipReasons[e.skipReason] ?? 0) + 1;
  const notes: string[] = [];
  const longSkipped = past.filter((e) => e.status !== 'cooked' && (getRecipe(e.recipeId)?.minutes ?? 0) >= 35).length;
  if (longSkipped >= 2) notes.push(`${longSkipped} dinners of 35 minutes or more didn’t happen.`);
  if ((skipReasons.no_time ?? 0) >= 2) notes.push(`${skipReasons.no_time} dinners were skipped for lack of time.`);
  if ((skipReasons.missing_ingredient ?? 0) >= 1) notes.push('A missing ingredient stopped a dinner. Check the shopping list before the week starts.');
  if (past.length >= 3 && cooked === past.length) notes.push('You cooked every planned dinner. Nice going.');
  return { planned: plan.entries.length, cooked, skipped, missed, skipReasons, notes };
}
